import { defineStore } from "pinia";
import { computed, ref } from "vue";
import * as brandingApi from "@/api/branding";
import defaultLogo from "@/assets/logo-technoprojects.svg";

/**
 * Site branding (logo + app name), editable by server admins.
 *
 * Defaults keep the UI rendering even before the (public) fetch resolves or if
 * it fails offline. `displayLogo` falls back to the bundled default mark.
 */
export const useBrandingStore = defineStore("branding", () => {
  const appName = ref("TechnoProjects");
  const logoUrl = ref<string | null>(null);
  const loaded = ref(false);

  const hasCustomLogo = computed(() => !!logoUrl.value);
  const displayLogo = computed(() => logoUrl.value || defaultLogo);

  function _apply(b: brandingApi.Branding) {
    appName.value = b.app_name || "TechnoProjects";
    logoUrl.value = b.logo;
    if (typeof document !== "undefined" && appName.value) {
      document.title = appName.value;
    }
  }

  async function fetch() {
    try {
      _apply(await brandingApi.getBranding());
    } catch {
      // Keep defaults on failure — the app must still render.
    } finally {
      loaded.value = true;
    }
  }

  async function save(form: FormData) {
    const b = await brandingApi.updateBranding(form);
    _apply(b);
    return b;
  }

  return { appName, logoUrl, loaded, hasCustomLogo, displayLogo, fetch, save };
});
